import { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import {
  Grid,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  CircularProgress,
} from '@mui/material'
import { Add, Edit, Delete, Person, Business, Security, Send, Refresh, Cancel, Settings, CloudUpload, CheckCircle, Error as ErrorIcon, ExpandMore, Notifications, NotificationsActive, VpnKey, ContentCopy, Block, DeleteForever } from '@mui/icons-material'
import { PageHeader, Card, Button, DataTable, Modal, StatusChip } from '@components/common'
import { useAuth } from '@context/AuthContext'
import { useBranding } from '@context/BrandingContext'
import api from '@services/api'
import { toast } from 'react-toastify'

// User Management Tab
const UsersTab = () => {
  const [users, setUsers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [inviteFormOpen, setInviteFormOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [activeSubTab, setActiveSubTab] = useState(0)
  const [editFormData, setEditFormData] = useState({
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    is_active: true,
    role_ids: [],
  })
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    department: '',
    job_title: '',
    phone_number: '',
    is_technician: false,
    is_staff: false,
    role_ids: [],
  })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersRes, invitesRes, deptsRes, rolesRes] = await Promise.all([
        api.get('/common/profiles/'),
        api.get('/auth/invitations/'),
        api.get('/common/departments/'),
        api.get('/common/roles/'),
      ])
      setUsers(usersRes.data.results || usersRes.data)
      setInvitations(invitesRes.data.results || invitesRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setRoles(rolesRes.data.results || rolesRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleInviteUser = () => {
    setInviteFormData({
      email: '',
      first_name: '',
      last_name: '',
      department: '',
      job_title: '',
      phone_number: '',
      is_technician: false,
      is_staff: false,
      role_ids: [],
    })
    setInviteFormOpen(true)
  }

  const handleEdit = (profile) => {
    setSelectedUser(profile)
    setEditFormData({
      department: profile.department || '',
      job_title: profile.job_title || '',
      phone_number: profile.phone_number || '',
      is_technician: profile.is_technician || false,
      is_active: profile.user?.is_active ?? true,
      role_ids: profile.roles?.map((r) => r.id) || [],
    })
    setEditFormOpen(true)
  }

  const handleSaveEdit = async () => {
    setSaving(true)
    try {
      await api.patch(`/common/profiles/${selectedUser.id}/`, {
        department: editFormData.department || null,
        job_title: editFormData.job_title,
        phone_number: editFormData.phone_number,
        is_technician: editFormData.is_technician,
        is_active: editFormData.is_active,
        role_ids: editFormData.role_ids,
      })
      toast.success('User updated successfully')
      setEditFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to update user. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleSendInvitation = async () => {
    setSaving(true)
    try {
      await api.post('/auth/invitations/', {
        email: inviteFormData.email,
        first_name: inviteFormData.first_name,
        last_name: inviteFormData.last_name,
        department: inviteFormData.department || null,
        job_title: inviteFormData.job_title,
        phone_number: inviteFormData.phone_number,
        is_technician: inviteFormData.is_technician,
        is_staff: inviteFormData.is_staff,
        role_ids: inviteFormData.role_ids,
      })
      toast.success('Invitation sent successfully!')
      setInviteFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error sending invitation:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.email?.[0]
        || 'Failed to send invitation. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleResendInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/resend/`)
      toast.success('Invitation resent successfully!')
    } catch (error) {
      toast.error('Failed to resend invitation.')
    }
  }

  const handleCancelInvitation = async (invitation) => {
    try {
      await api.post(`/auth/invitations/${invitation.id}/cancel/`)
      toast.success('Invitation cancelled.')
      fetchData()
    } catch (error) {
      toast.error('Failed to cancel invitation.')
    }
  }

  const handleDelete = async () => {
    try {
      await api.delete(`/common/users/${selectedUser.user.id}/`)
      setDeleteModalOpen(false)
      toast.success('User deleted successfully')
      fetchData()
    } catch (error) {
      console.error('Error deleting user:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || 'Failed to delete user. Please try again.'
      toast.error(errorMessage)
    }
  }

  const userColumns = [
    {
      field: 'user',
      headerName: 'Name',
      renderCell: (value) => `${value?.first_name || ''} ${value?.last_name || ''}`.trim() || value?.username,
    },
    { field: 'user', headerName: 'Username', renderCell: (value) => value?.username },
    { field: 'user', headerName: 'Email', renderCell: (value) => value?.email || '-' },
    { field: 'department_name', headerName: 'Section/Unit', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'is_technician',
      headerName: 'Technician',
      renderCell: (value) => value ? <Chip label="Yes" size="small" color="primary" /> : <Chip label="No" size="small" />,
    },
    {
      field: 'roles',
      headerName: 'Roles',
      renderCell: (value) => value?.map((r) => r.name).join(', ') || '-',
    },
    {
      field: 'user',
      headerName: 'Status',
      renderCell: (value) => (
        <StatusChip status={value?.is_active ? 'active' : 'inactive'} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setSelectedUser(row); setDeleteModalOpen(true) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const invitationColumns = [
    { field: 'email', headerName: 'Email' },
    {
      field: 'name',
      headerName: 'Name',
      renderCell: (_, row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
    },
    { field: 'department_name', headerName: 'Section/Unit', renderCell: (value) => value || '-' },
    { field: 'job_title', headerName: 'Job Title', renderCell: (value) => value || '-' },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (value) => (
        <Chip
          label={value}
          size="small"
          color={value === 'pending' ? 'warning' : value === 'accepted' ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Sent',
      renderCell: (value) => formatDate(value),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => row.status === 'pending' && (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            title="Resend invitation"
            onClick={(e) => { e.stopPropagation(); handleResendInvitation(row) }}
          >
            <Refresh fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Cancel invitation"
            onClick={(e) => { e.stopPropagation(); handleCancelInvitation(row) }}
          >
            <Cancel fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  const pendingInvitations = invitations.filter(i => i.status === 'pending')

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={activeSubTab} onChange={(_, v) => setActiveSubTab(v)}>
          <Tab label={`Users (${users.length})`} />
          <Tab label={`Pending Invitations (${pendingInvitations.length})`} />
        </Tabs>
        <Button startIcon={<Send />} onClick={handleInviteUser}>Invite User</Button>
      </Box>

      {activeSubTab === 0 && (
        <DataTable
          columns={userColumns}
          data={users}
          loading={loading}
          searchPlaceholder="Search users..."
          emptyMessage="No users found"
        />
      )}

      {activeSubTab === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Users will receive an email invitation and must set their own password when accepting.
          </Alert>
          <DataTable
            columns={invitationColumns}
            data={pendingInvitations}
            loading={loading}
            searchPlaceholder="Search invitations..."
            emptyMessage="No pending invitations"
          />
        </>
      )}

      {/* Invite User Modal */}
      <Modal
        open={inviteFormOpen}
        onClose={() => setInviteFormOpen(false)}
        title="Invite New User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setInviteFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSendInvitation} loading={saving} startIcon={<Send />}>Send Invitation</Button>
          </>
        }
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          The user will receive an email with a link to set up their account and create their own password.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteFormData.email}
              onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
              required
              helperText="Invitation will be sent to this email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={inviteFormData.first_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, first_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={inviteFormData.last_name}
              onChange={(e) => setInviteFormData({ ...inviteFormData, last_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Section/Unit</InputLabel>
              <Select
                value={inviteFormData.department}
                onChange={(e) => setInviteFormData({ ...inviteFormData, department: e.target.value })}
                label="Section/Unit"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={inviteFormData.job_title}
              onChange={(e) => setInviteFormData({ ...inviteFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={inviteFormData.phone_number}
              onChange={(e) => setInviteFormData({ ...inviteFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_technician}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={inviteFormData.role_ids}
                onChange={(e) => setInviteFormData({ ...inviteFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={inviteFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteFormData.is_staff}
                  onChange={(e) => setInviteFormData({ ...inviteFormData, is_staff: e.target.checked })}
                />
              }
              label="Admin Access (Staff)"
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        title="Edit User"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} loading={saving}>Update</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Section/Unit</InputLabel>
              <Select
                value={editFormData.department}
                onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                label="Section/Unit"
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              value={editFormData.job_title}
              onChange={(e) => setEditFormData({ ...editFormData, job_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={editFormData.phone_number}
              onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_technician}
                  onChange={(e) => setEditFormData({ ...editFormData, is_technician: e.target.checked })}
                />
              }
              label="Is Technician"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={editFormData.role_ids}
                onChange={(e) => setEditFormData({ ...editFormData, role_ids: e.target.value })}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => roles.filter((r) => selected.includes(r.id)).map((r) => r.name).join(', ')}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={editFormData.role_ids.includes(role.id)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active}
                  onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                  color={editFormData.is_active ? 'success' : 'default'}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Account Active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {editFormData.is_active
                      ? 'User can log in and access the system'
                      : 'User is blocked from logging in'}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete User"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>Delete</Button>
          </>
        }
      >
        <Typography>Are you sure you want to delete "{selectedUser?.user?.username}"?</Typography>
      </Modal>
    </>
  )
}

// Section/Unit Management Tab
const DepartmentsTab = () => {
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedDept, setSelectedDept] = useState(null)
  const [formData, setFormData] = useState({ name: '', code: '', description: '', is_active: true })
  const [saving, setSaving] = useState(false)

  const fetchData = async () => {
    try {
      const response = await api.get('/common/departments/')
      setDepartments(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching departments:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedDept(null)
    setFormData({ name: '', code: '', description: '', is_active: true })
    setFormOpen(true)
  }

  const handleEdit = (dept) => {
    setSelectedDept(dept)
    setFormData({
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      is_active: dept.is_active,
    })
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedDept) {
        await api.patch(`/common/departments/${selectedDept.id}/`, formData)
        toast.success('Section/Unit updated successfully')
      } else {
        await api.post('/common/departments/', formData)
        toast.success('Section/Unit created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving department:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.code?.[0]
        || error.response?.data?.name?.[0]
        || 'Failed to save department. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'code', headerName: 'Code', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    { field: 'member_count', headerName: 'Members' },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Section/Unit</Button>
      </Box>
      <DataTable
        columns={columns}
        data={departments}
        loading={loading}
        searchPlaceholder="Search departments..."
        emptyMessage="No departments found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedDept ? 'Edit Section/Unit' : 'Add Section/Unit'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedDept ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Section/Unit Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Roles Management Tab
const RolesTab = () => {
  const [roles, setRoles] = useState([])
  const [availablePermissions, setAvailablePermissions] = useState({})
  const [permissionCategories, setPermissionCategories] = useState({})
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true, permissions: [] })
  const [saving, setSaving] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/common/roles/'),
        api.get('/auth/permissions/available/'),
      ])
      setRoles(rolesRes.data.results || rolesRes.data)
      setAvailablePermissions(permsRes.data.permissions || {})
      setPermissionCategories(permsRes.data.categories || {})
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedRole(null)
    setFormData({ name: '', description: '', is_active: true, permissions: [] })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleEdit = (role) => {
    setSelectedRole(role)
    setFormData({
      name: role.name,
      description: role.description || '',
      is_active: role.is_active,
      permissions: role.permissions || [],
    })
    setExpandedCategories({})
    setFormOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (selectedRole) {
        await api.patch(`/common/roles/${selectedRole.id}/`, formData)
        toast.success('Role updated successfully')
      } else {
        await api.post('/common/roles/', formData)
        toast.success('Role created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving role:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || 'Failed to save role. Please try again.'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const togglePermission = (permCode) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permCode)
        ? prev.permissions.filter((p) => p !== permCode)
        : [...prev.permissions, permCode],
    }))
  }

  const toggleCategory = (categoryPerms) => {
    const permCodes = categoryPerms.map((p) => p.code)
    const allSelected = permCodes.every((p) => formData.permissions.includes(p))
    if (allSelected) {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !permCodes.includes(p)),
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...permCodes])],
      }))
    }
  }

  const allPermissionCodes = Object.values(permissionCategories).flat().map((p) => p.code)
  const allPermissionsSelected = allPermissionCodes.length > 0
    && allPermissionCodes.every((p) => formData.permissions.includes(p))
  const somePermissionsSelected = allPermissionCodes.some((p) => formData.permissions.includes(p))

  const toggleAllPermissions = () => {
    setFormData((prev) => ({
      ...prev,
      permissions: allPermissionsSelected ? [] : [...allPermissionCodes],
    }))
  }

  const columns = [
    { field: 'name', headerName: 'Role Name', sortable: true },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    {
      field: 'permissions',
      headerName: 'Permissions',
      renderCell: (value) => (
        <Chip label={`${value?.length || 0} permissions`} size="small" variant="outlined" />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Role</Button>
      </Box>
      <DataTable
        columns={columns}
        data={roles}
        loading={loading}
        searchPlaceholder="Search roles..."
        emptyMessage="No roles found"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedRole ? 'Edit Role' : 'Add Role'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selectedRole ? 'Update' : 'Create'}</Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Permissions ({formData.permissions.length} selected)
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allPermissionsSelected}
                    indeterminate={somePermissionsSelected && !allPermissionsSelected}
                    onChange={toggleAllPermissions}
                  />
                }
                label={allPermissionsSelected ? 'Deselect All' : 'Select All'}
              />
            </Box>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {Object.entries(permissionCategories).map(([category, perms]) => {
                const allSelected = perms.every((p) => formData.permissions.includes(p.code))
                const someSelected = perms.some((p) => formData.permissions.includes(p.code))
                return (
                  <Box key={category} mb={2}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => toggleCategory(perms)}
                      />
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedCategories((prev) => ({
                          ...prev,
                          [category]: !prev[category],
                        }))}
                      >
                        {category} ({perms.filter((p) => formData.permissions.includes(p.code)).length}/{perms.length})
                      </Typography>
                    </Box>
                    <Box pl={4}>
                      {perms.map((perm) => (
                        <FormControlLabel
                          key={perm.code}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.permissions.includes(perm.code)}
                              onChange={() => togglePermission(perm.code)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {perm.name} <Typography component="span" variant="caption" color="text.secondary">({perm.code})</Typography>
                            </Typography>
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Grid>
        </Grid>
      </Modal>
    </>
  )
}

// Notification Settings Tab
const NotificationSettingsTab = () => {
  const [configsByCategory, setConfigsByCategory] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [users, setUsers] = useState([])
  const [departments, setDepartments] = useState([])
  const [roles, setRoles] = useState([])
  const [editFormData, setEditFormData] = useState({
    use_default_recipients: true,
    is_enabled: true,
    allow_push: true,
    allow_email: true,
    include_all_admins: false,
    include_all_technicians: false,
    additional_user_ids: [],
    additional_department_ids: [],
    additional_role_ids: [],
  })

  const fetchConfigs = async () => {
    try {
      setLoading(true)
      const response = await api.get('/common/notification-configs/by-category/')
      setConfigsByCategory(response.data)
    } catch (error) {
      console.error('Error fetching notification configs:', error)
      toast.error('Failed to load notification settings')
    } finally {
      setLoading(false)
    }
  }

  const fetchOptions = async () => {
    try {
      const [usersRes, deptsRes, rolesRes] = await Promise.all([
        api.get('/common/profiles/'),
        api.get('/common/departments/'),
        api.get('/common/roles/'),
      ])
      setUsers(usersRes.data.results || usersRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setRoles(rolesRes.data.results || rolesRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  useEffect(() => {
    fetchConfigs()
    fetchOptions()
  }, [])

  const handleToggleEnabled = async (config, enabled) => {
    try {
      await api.patch(`/common/notification-configs/${config.id}/`, { is_enabled: enabled })
      fetchConfigs()
      toast.success(`${config.display_name} ${enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      toast.error('Failed to update notification setting')
    }
  }

  const handleEditConfig = async (config) => {
    try {
      // Fetch full config details
      const response = await api.get(`/common/notification-configs/${config.id}/`)
      const fullConfig = response.data
      setSelectedConfig(fullConfig)
      setEditFormData({
        use_default_recipients: fullConfig.use_default_recipients,
        is_enabled: fullConfig.is_enabled,
        allow_push: fullConfig.allow_push,
        allow_email: fullConfig.allow_email,
        include_all_admins: fullConfig.include_all_admins,
        include_all_technicians: fullConfig.include_all_technicians,
        additional_user_ids: fullConfig.additional_user_ids || [],
        additional_department_ids: fullConfig.additional_department_ids || [],
        additional_role_ids: fullConfig.additional_role_ids || [],
      })
      setEditModalOpen(true)
    } catch (error) {
      toast.error('Failed to load notification config')
    }
  }

  const handleSaveConfig = async () => {
    setSaving(true)
    try {
      await api.patch(`/common/notification-configs/${selectedConfig.id}/`, editFormData)
      toast.success('Notification settings updated')
      setEditModalOpen(false)
      fetchConfigs()
    } catch (error) {
      console.error('Error saving config:', error)
      toast.error('Failed to save notification settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 3 }}>
        Configure which notifications are sent and who receives them. In-app notifications are always sent
        and cannot be disabled. You can add additional recipients beyond the default ones for each notification type.
      </Alert>

      {Object.entries(configsByCategory).map(([category, { label, configs }]) => (
        <Accordion key={category} defaultExpanded={category === 'work_orders' || category === 'fault_reports'}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <NotificationsActive fontSize="small" color="primary" />
              <Typography fontWeight="medium">{label}</Typography>
              <Chip
                label={`${configs.filter(c => c.is_enabled).length}/${configs.length} enabled`}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {configs.map((config) => (
                <Box
                  key={config.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                    bgcolor: config.is_enabled ? 'transparent' : 'action.hover',
                    opacity: config.is_enabled ? 1 : 0.7,
                  }}
                >
                  <Box flex={1}>
                    <Typography variant="body1" fontWeight="medium">
                      {config.display_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {config.description}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.5}>
                      {config.use_default_recipients && (
                        <Chip
                          label={`Default: ${config.default_recipients_description}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {(config.additional_users_count > 0 || config.additional_departments_count > 0 || config.additional_roles_count > 0) && (
                        <Chip
                          label={`+${config.additional_users_count + config.additional_departments_count + config.additional_roles_count} additional`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                      {config.include_all_admins && (
                        <Chip label="All admins" size="small" color="warning" variant="outlined" />
                      )}
                      {config.include_all_technicians && (
                        <Chip label="All technicians" size="small" color="info" variant="outlined" />
                      )}
                      {!config.allow_push && (
                        <Chip label="Push disabled" size="small" color="default" variant="outlined" />
                      )}
                      {!config.allow_email && (
                        <Chip label="Email disabled" size="small" color="default" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditConfig(config)}
                      title="Configure recipients"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <Switch
                      checked={config.is_enabled}
                      onChange={(e) => handleToggleEnabled(config, e.target.checked)}
                      color="primary"
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Edit Configuration Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={`Configure: ${selectedConfig?.display_name}`}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig} loading={saving}>Save Changes</Button>
          </>
        }
      >
        {selectedConfig && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedConfig.description}
              </Typography>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Enable/Disable and Channels */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.is_enabled}
                    onChange={(e) => setEditFormData({ ...editFormData, is_enabled: e.target.checked })}
                  />
                }
                label="Notification Enabled"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" gap={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.allow_push}
                      onChange={(e) => setEditFormData({ ...editFormData, allow_push: e.target.checked })}
                    />
                  }
                  label="Push"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.allow_email}
                      onChange={(e) => setEditFormData({ ...editFormData, allow_email: e.target.checked })}
                    />
                  }
                  label="Email"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                In-app notifications are always enabled
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Default Recipients
              </Typography>
            </Grid>

            {/* Default Recipients Toggle */}
            <Grid item xs={12}>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.use_default_recipients}
                      onChange={(e) => setEditFormData({ ...editFormData, use_default_recipients: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Use Default Recipients</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedConfig.default_recipients_description}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Additional Recipients
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Add more users, departments, or roles to receive this notification (in addition to default recipients).
              </Typography>
            </Grid>

            {/* Quick Add Options */}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.include_all_admins}
                    onChange={(e) => setEditFormData({ ...editFormData, include_all_admins: e.target.checked })}
                  />
                }
                label="Include all administrators"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editFormData.include_all_technicians}
                    onChange={(e) => setEditFormData({ ...editFormData, include_all_technicians: e.target.checked })}
                  />
                }
                label="Include all technicians"
              />
            </Grid>

            {/* Additional Users */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={users}
                value={users.filter(u => editFormData.additional_user_ids.includes(u.user?.id))}
                onChange={(_, newValue) => setEditFormData({
                  ...editFormData,
                  additional_user_ids: newValue.map(u => u.user?.id)
                })}
                getOptionLabel={(option) => {
                  const name = `${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim()
                  return name || option.user?.username || ''
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Additional Users" placeholder="Select users..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.user?.id}
                      label={`${option.user?.first_name || ''} ${option.user?.last_name || ''}`.trim() || option.user?.username}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>

            {/* Additional Sections/Units */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={departments}
                value={departments.filter(d => editFormData.additional_department_ids.includes(d.id))}
                onChange={(_, newValue) => setEditFormData({
                  ...editFormData,
                  additional_department_ids: newValue.map(d => d.id)
                })}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Additional Sections/Units" placeholder="Select sections/units..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
              />
              <Typography variant="caption" color="text.secondary">
                All users in selected departments will receive this notification
              </Typography>
            </Grid>

            {/* Additional Roles */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={roles}
                value={roles.filter(r => editFormData.additional_role_ids.includes(r.id))}
                onChange={(_, newValue) => setEditFormData({
                  ...editFormData,
                  additional_role_ids: newValue.map(r => r.id)
                })}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Additional Roles" placeholder="Select roles..." />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
              />
              <Typography variant="caption" color="text.secondary">
                All users with selected roles will receive this notification
              </Typography>
            </Grid>
          </Grid>
        )}
      </Modal>
    </>
  )
}

// System Credentials Tab (FCM, etc.)
const CredentialsTab = () => {
  const [credentials, setCredentials] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedCredential, setSelectedCredential] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    credential_type: 'fcm_service_account',
    description: '',
    credentials_json: null,
    is_active: true,
  })
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [fileName, setFileName] = useState('')

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await api.get('/common/credentials/')
      setCredentials(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching credentials:', error)
      if (error.response?.status === 403) {
        toast.error('You need admin privileges to manage credentials')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedCredential(null)
    setFormData({
      name: '',
      credential_type: 'fcm_service_account',
      description: '',
      credentials_json: null,
      is_active: true,
    })
    setFileName('')
    setTestResult(null)
    setFormOpen(true)
  }

  const handleEdit = async (credential) => {
    try {
      // Fetch full credential details including the JSON
      const response = await api.get(`/common/credentials/${credential.id}/`)
      const fullCredential = response.data
      setSelectedCredential(fullCredential)
      setFormData({
        name: fullCredential.name,
        credential_type: fullCredential.credential_type,
        description: fullCredential.description || '',
        credentials_json: fullCredential.credentials_json,
        is_active: fullCredential.is_active,
      })
      setFileName('Current credentials loaded')
      setTestResult(null)
      setFormOpen(true)
    } catch (error) {
      toast.error('Failed to load credential details')
    }
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a JSON file')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result)
        // Validate FCM service account fields
        const requiredFields = ['type', 'project_id', 'private_key', 'client_email']
        const missing = requiredFields.filter(f => !json[f])
        if (missing.length > 0) {
          toast.error(`Invalid FCM service account JSON. Missing: ${missing.join(', ')}`)
          return
        }
        setFormData(prev => ({ ...prev, credentials_json: json }))
        setFileName(file.name)
        setTestResult(null)
        toast.success('Service account JSON loaded successfully')
      } catch (err) {
        toast.error('Invalid JSON file')
      }
    }
    reader.readAsText(file)
  }

  const handleTest = async () => {
    if (!selectedCredential?.id) {
      toast.error('Save the credential first before testing')
      return
    }
    setTesting(true)
    setTestResult(null)
    try {
      const response = await api.post(`/common/credentials/${selectedCredential.id}/test/`)
      setTestResult({ success: true, message: response.data.message, project_id: response.data.project_id })
      toast.success('FCM credentials are valid!')
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Test failed'
      setTestResult({ success: false, message: errorMsg })
      toast.error(errorMsg)
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Name is required')
      return
    }
    if (!formData.credentials_json) {
      toast.error('Please upload a service account JSON file')
      return
    }

    setSaving(true)
    try {
      if (selectedCredential) {
        await api.put(`/common/credentials/${selectedCredential.id}/`, formData)
        toast.success('Credential updated successfully')
      } else {
        await api.post('/common/credentials/', formData)
        toast.success('Credential created successfully')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving credential:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.credentials_json?.[0]
        || error.response?.data?.name?.[0]
        || 'Failed to save credential'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedCredential) return
    try {
      await api.delete(`/common/credentials/${selectedCredential.id}/`)
      toast.success('Credential deleted')
      setFormOpen(false)
      fetchData()
    } catch (error) {
      toast.error('Failed to delete credential')
    }
  }

  const columns = [
    { field: 'name', headerName: 'Name', sortable: true },
    { field: 'credential_type_display', headerName: 'Type' },
    { field: 'description', headerName: 'Description', renderCell: (v) => v || '-' },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      renderCell: (value) => formatDateTime(value),
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
          <Edit fontSize="small" />
        </IconButton>
      ),
    },
  ]

  const fcmCredential = credentials.find(c => c.credential_type === 'fcm_service_account' && c.is_active)

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Upload your Firebase Cloud Messaging (FCM) service account JSON to enable push notifications.
        You can download this from the Firebase Console under Project Settings → Service Accounts.
      </Alert>

      {!fcmCredential && credentials.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No FCM credentials configured. Push notifications will not work until you add your Firebase service account.
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Add Credential</Button>
      </Box>

      <DataTable
        columns={columns}
        data={credentials}
        loading={loading}
        searchPlaceholder="Search credentials..."
        emptyMessage="No credentials configured"
      />

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedCredential ? 'Edit Credential' : 'Add FCM Credential'}
        maxWidth="sm"
        actions={
          <>
            {selectedCredential && (
              <Button variant="outlined" color="error" onClick={handleDelete} sx={{ mr: 'auto' }}>
                Delete
              </Button>
            )}
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedCredential ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Production FCM"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Type"
              value={formData.credential_type}
              onChange={(e) => setFormData({ ...formData, credential_type: e.target.value })}
            >
              <MenuItem value="fcm_service_account">Firebase Cloud Messaging</MenuItem>
              <MenuItem value="smtp">SMTP Configuration</MenuItem>
              <MenuItem value="sms_gateway">SMS Gateway</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              placeholder="Optional description"
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: formData.credentials_json ? 'success.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: formData.credentials_json ? 'success.50' : 'background.default',
              }}
            >
              <input
                type="file"
                accept=".json"
                id="fcm-file-upload"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <label htmlFor="fcm-file-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                >
                  Upload Service Account JSON
                </Button>
              </label>
              {fileName && (
                <Typography variant="body2" color="success.main" mt={1}>
                  {fileName}
                </Typography>
              )}
              {formData.credentials_json?.project_id && (
                <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                  Project ID: {formData.credentials_json.project_id}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Grid>
          {selectedCredential && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleTest}
                  loading={testing}
                  disabled={testing}
                >
                  Test Connection
                </Button>
                {testResult && (
                  <Box display="flex" alignItems="center" gap={1}>
                    {testResult.success ? (
                      <CheckCircle color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                    <Typography
                      variant="body2"
                      color={testResult.success ? 'success.main' : 'error.main'}
                    >
                      {testResult.message}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Modal>
    </>
  )
}

// API Keys Management Tab
const APIKeysTab = () => {
  const [apiKeys, setApiKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [revokeModalOpen, setRevokeModalOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    scope: 'read',
    expires_at: '',
  })
  const [saving, setSaving] = useState(false)
  const [newKeyData, setNewKeyData] = useState(null)
  const [revokeReason, setRevokeReason] = useState('')

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await api.get('/common/api-keys/')
      setApiKeys(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching API keys:', error)
      if (error.response?.status === 403) {
        toast.error('You need admin privileges to manage API keys')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setFormData({
      name: '',
      scope: 'read',
      expires_at: '',
    })
    setNewKeyData(null)
    setFormOpen(true)
  }

  const handleCopyKey = () => {
    if (newKeyData?.key) {
      navigator.clipboard.writeText(newKeyData.key)
      toast.success('API key copied to clipboard!')
    }
  }

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Name is required')
      return
    }

    setSaving(true)
    try {
      const payload = {
        name: formData.name,
        scope: formData.scope,
      }
      if (formData.expires_at) {
        payload.expires_at = formData.expires_at
      }

      const response = await api.post('/common/api-keys/', payload)
      setNewKeyData(response.data)
      toast.success('API key created successfully! Copy it now - it will not be shown again.')
      fetchData()
    } catch (error) {
      console.error('Error saving API key:', error)
      const errorMessage = error.response?.data?.error
        || error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || 'Failed to create API key'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleRevoke = async () => {
    if (!selectedKey) return
    setSaving(true)
    try {
      await api.post(`/common/api-keys/${selectedKey.id}/revoke/`, {
        reason: revokeReason || 'Revoked by administrator',
      })
      toast.success('API key revoked successfully')
      setRevokeModalOpen(false)
      setRevokeReason('')
      fetchData()
    } catch (error) {
      console.error('Error revoking API key:', error)
      toast.error('Failed to revoke API key')
    } finally {
      setSaving(false)
    }
  }

  const handleReactivate = async (key) => {
    try {
      await api.post(`/common/api-keys/${key.id}/reactivate/`)
      toast.success('API key reactivated successfully')
      fetchData()
    } catch (error) {
      toast.error('Failed to reactivate API key')
    }
  }

  const handleDelete = async () => {
    if (!selectedKey) return
    try {
      await api.delete(`/common/api-keys/${selectedKey.id}/`)
      toast.success('API key deleted successfully')
      setDeleteModalOpen(false)
      fetchData()
    } catch (error) {
      toast.error('Failed to delete API key')
    }
  }

  const getScopeColor = (scope) => {
    switch (scope) {
      case 'read': return 'info'
      case 'write': return 'warning'
      case 'admin': return 'error'
      default: return 'default'
    }
  }

  const columns = [
    { field: 'name', headerName: 'Name', sortable: true },
    {
      field: 'prefix',
      headerName: 'Key Prefix',
      renderCell: (value) => (
        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
          {value}••••••••
        </Typography>
      ),
    },
    {
      field: 'scope',
      headerName: 'Scope',
      renderCell: (value) => (
        <Chip label={value} size="small" color={getScopeColor(value)} />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value ? 'active' : 'inactive'} />,
    },
    {
      field: 'last_used_at',
      headerName: 'Last Used',
      renderCell: (value) => value ? formatDateTime(value) : 'Never',
    },
    {
      field: 'expires_at',
      headerName: 'Expires',
      renderCell: (value) => value ? formatDate(value) : 'Never',
    },
    {
      field: 'created_by_name',
      headerName: 'Created By',
      renderCell: (value) => value || '-',
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: (_, row) => (
        <Box display="flex" gap={1}>
          {row.is_active ? (
            <IconButton
              size="small"
              color="warning"
              title="Revoke"
              onClick={(e) => {
                e.stopPropagation()
                setSelectedKey(row)
                setRevokeModalOpen(true)
              }}
            >
              <Block fontSize="small" />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              color="success"
              title="Reactivate"
              onClick={(e) => {
                e.stopPropagation()
                handleReactivate(row)
              }}
            >
              <CheckCircle fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            color="error"
            title="Delete"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedKey(row)
              setDeleteModalOpen(true)
            }}
          >
            <DeleteForever fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        API keys allow external applications to authenticate with the DuoNep API without user credentials.
        Each key can have read, write, or admin permissions. Keep your API keys secure - they provide direct access to your system.
      </Alert>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Security Best Practices:</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
          <li>Never commit API keys to version control</li>
          <li>Use read-only keys when possible</li>
          <li>Set expiration dates for temporary access</li>
          <li>Revoke unused keys immediately</li>
          <li>Monitor key usage regularly</li>
        </ul>
      </Alert>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button startIcon={<Add />} onClick={handleAdd}>Create API Key</Button>
      </Box>

      <DataTable
        columns={columns}
        data={apiKeys}
        loading={loading}
        searchPlaceholder="Search API keys..."
        emptyMessage="No API keys created yet"
      />

      {/* Create API Key Modal */}
      <Modal
        open={formOpen}
        onClose={() => {
          if (newKeyData) {
            setFormOpen(false)
            setNewKeyData(null)
          } else {
            setFormOpen(false)
          }
        }}
        title={newKeyData ? 'API Key Created' : 'Create API Key'}
        maxWidth="sm"
        actions={
          newKeyData ? (
            <>
              <Button variant="outlined" onClick={() => { setFormOpen(false); setNewKeyData(null) }}>
                Close
              </Button>
              <Button startIcon={<ContentCopy />} onClick={handleCopyKey}>
                Copy API Key
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} loading={saving}>Create</Button>
            </>
          )
        }
      >
        {newKeyData ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Important:</strong> This is the only time you'll see this API key. Copy it now and store it securely.
              If you lose it, you'll need to create a new one.
            </Alert>
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                mb: 2,
              }}
            >
              <Typography variant="body2" fontWeight="bold" mb={1}>
                API Key:
              </Typography>
              <Typography variant="body2" sx={{ userSelect: 'all' }}>
                {newKeyData.key}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>Key Name:</strong> {newKeyData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Scope:</strong> {newKeyData.scope}
            </Typography>
            {newKeyData.expires_at && (
              <Typography variant="body2" color="text.secondary">
                <strong>Expires:</strong> {formatDate(newKeyData.expires_at)}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Use this key in your HTTP requests by adding the header:
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.900',
                color: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                mt: 1,
              }}
            >
              X-API-Key: {newKeyData.key}
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., External Monitoring Service"
                helperText="Descriptive name to identify this API key"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  label="Scope"
                >
                  <MenuItem value="read">
                    <Box>
                      <Typography variant="body2" fontWeight="medium">Read Only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can only view data, no modifications
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="write">
                    <Box>
                      <Typography variant="body2" fontWeight="medium">Read & Write</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can view and modify data
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box>
                      <Typography variant="body2" fontWeight="medium" color="error">Admin Access</Typography>
                      <Typography variant="caption" color="error">
                        Full access including user management
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Expiration Date (Optional)"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for no expiration"
                inputProps={{
                  min: new Date().toISOString().split('T')[0],
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                The API key will be generated when you click Create. Make sure to copy it immediately as it won't be shown again.
              </Alert>
            </Grid>
          </Grid>
        )}
      </Modal>

      {/* Revoke API Key Modal */}
      <Modal
        open={revokeModalOpen}
        onClose={() => { setRevokeModalOpen(false); setRevokeReason('') }}
        title="Revoke API Key"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => { setRevokeModalOpen(false); setRevokeReason('') }}>
              Cancel
            </Button>
            <Button color="warning" onClick={handleRevoke} loading={saving}>
              Revoke
            </Button>
          </>
        }
      >
        <Typography gutterBottom>
          Are you sure you want to revoke the API key <strong>"{selectedKey?.name}"</strong>?
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          This will immediately stop all API requests using this key. You can reactivate it later if needed.
        </Typography>
        <TextField
          fullWidth
          label="Reason (Optional)"
          value={revokeReason}
          onChange={(e) => setRevokeReason(e.target.value)}
          multiline
          rows={2}
          placeholder="e.g., Security concern, key compromised"
          sx={{ mt: 2 }}
        />
      </Modal>

      {/* Delete API Key Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete API Key"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>Delete Permanently</Button>
          </>
        }
      >
        <Typography gutterBottom>
          Are you sure you want to permanently delete the API key <strong>"{selectedKey?.name}"</strong>?
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          <strong>Warning:</strong> This action cannot be undone. All API requests using this key will fail immediately.
        </Alert>
      </Modal>
    </>
  )
}

// Settings Tab with Sub-tabs
// Organization Branding Tab - co-brand editor (org name/subtitle/contact + logo)
const BrandingTab = () => {
  const { refreshBranding } = useBranding()
  const [form, setForm] = useState({
    organization_name: '',
    organization_short_name: '',
    system_subtitle: '',
    contact_email: '',
    contact_phone: '',
  })
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const { data } = await api.get('/common/branding/')
        setForm({
          organization_name: data.organization_name || '',
          organization_short_name: data.organization_short_name || '',
          system_subtitle: data.system_subtitle || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
        })
        setLogoPreview(data.organization_logo || null)
      } catch (error) {
        console.error('Failed to load branding:', error)
        toast.error('Failed to load branding settings')
      } finally {
        setLoading(false)
      }
    }
    fetchBranding()
  }, [])

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLogoFile(file)
    setLogoPreview(URL.createObjectURL(file))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = new FormData()
      Object.entries(form).forEach(([key, value]) => payload.append(key, value))
      if (logoFile) {
        payload.append('organization_logo', logoFile)
      }
      await api.patch('/common/branding/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast.success('Branding updated successfully')
      setLogoFile(null)
      await refreshBranding()
    } catch (error) {
      console.error('Failed to save branding:', error)
      toast.error(error.response?.data?.detail || 'Failed to update branding')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Card>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Organization Branding
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The organization name and logo appear on documents, PDFs, emails and partnership areas of the app.
          The app itself remains DuoNep branded.
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Organization Name"
            value={form.organization_name}
            onChange={handleChange('organization_name')}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Short Name / Abbreviation"
            value={form.organization_short_name}
            onChange={handleChange('organization_short_name')}
            margin="normal"
            helperText="e.g. UCI"
          />
          <TextField
            fullWidth
            label="System Subtitle"
            value={form.system_subtitle}
            onChange={handleChange('system_subtitle')}
            margin="normal"
            helperText="Shown under the organization name on documents"
          />
          <TextField
            fullWidth
            label="Contact Email"
            value={form.contact_email}
            onChange={handleChange('contact_email')}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Contact Phone"
            value={form.contact_phone}
            onChange={handleChange('contact_phone')}
            margin="normal"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Organization Logo
          </Typography>
          <Box
            sx={{
              border: '1px dashed #c4cdd5',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              minHeight: 180,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {logoPreview ? (
              <Box
                component="img"
                src={logoPreview}
                alt="Organization logo"
                sx={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No logo uploaded
              </Typography>
            )}
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
            >
              Upload Logo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleLogoSelect}
              />
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleSave} loading={saving} startIcon={<CheckCircle />}>
          Save Branding
        </Button>
      </Box>
    </Card>
  )
}

const SettingsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState(0)

  return (
    <>
      <Tabs
        value={activeSubTab}
        onChange={(_, v) => setActiveSubTab(v)}
        sx={{ mb: 3 }}
      >
        <Tab icon={<Business />} iconPosition="start" label="Branding" />
        <Tab icon={<Notifications />} iconPosition="start" label="Notifications" />
        <Tab icon={<Security />} iconPosition="start" label="Credentials" />
        <Tab icon={<VpnKey />} iconPosition="start" label="API Keys" />
      </Tabs>

      {activeSubTab === 0 && <BrandingTab />}
      {activeSubTab === 1 && <NotificationSettingsTab />}
      {activeSubTab === 2 && <CredentialsTab />}
      {activeSubTab === 3 && <APIKeysTab />}
    </>
  )
}

// Main Administration Page
const Administration = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  // Determine active tab based on URL
  const activeTab = useMemo(() => {
    if (location.pathname.includes('/administration/departments')) return 1
    if (location.pathname.includes('/administration/roles')) return 2
    if (location.pathname.includes('/administration/settings')) return 3
    return 0 // Default to users
  }, [location.pathname])

  const handleTabChange = (_, newValue) => {
    const routes = ['/administration/users', '/administration/departments', '/administration/roles', '/administration/settings']
    navigate(routes[newValue])
  }

  // Redirect non-admins
  if (!user?.is_superuser && !user?.is_staff) {
    return (
      <Box>
        <PageHeader
          title="Administration"
          subtitle="Access denied"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Administration' },
          ]}
        />
        <Card>
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              You need administrator privileges to access this section.
            </Typography>
          </Box>
        </Card>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Administration"
        subtitle="Manage users, sections/units, roles, and settings"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Administration' },
        ]}
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Person />} iconPosition="start" label="Users" />
          <Tab icon={<Business />} iconPosition="start" label="Sections/Units" />
          <Tab icon={<Security />} iconPosition="start" label="Roles" />
          <Tab icon={<Settings />} iconPosition="start" label="Settings" />
        </Tabs>

        {activeTab === 0 && <UsersTab />}
        {activeTab === 1 && <DepartmentsTab />}
        {activeTab === 2 && <RolesTab />}
        {activeTab === 3 && <SettingsTab />}
      </Card>
    </Box>
  )
}

export default Administration
